/**
 * The calendar's schedule actions and robot problems, shared by the phone
 * calendar (Now / Today / Schedules) and the laptop Agenda.
 *
 * Moved here unchanged from `PhoneCalendarContainer` (2026-10-30) so the
 * two read the same endpoints with the same gates:
 *
 *  - ACTIONS. "Start now" / "Run next now" is `POST .../occurrences/
 *    {start}/run` (`useRunOccurrenceNow`), "Skip" is the occurrence skip,
 *    Pause / Resume is the schedule's `enabled`. They are built ONLY when
 *    the screen says this viewer may edit schedules
 *    (`scheduleEditsAllowed`: role + flag + readiness, the very value the
 *    lanes' skip / pause callbacks are gated on); otherwise they are
 *    absent and nothing draws a button for them. While previewing another
 *    role (`useActionsOff`) they are drawn but off, and while fire mode
 *    holds the site nothing is started -- the hold is said, not tried.
 *    Pause or Resume of the rows of one plan together ("Pause both") is
 *    one request per row (`plan-batch`): when one fails, the status says
 *    which rows changed and which did not, and the schedules are read
 *    again so each is drawn as it now is.
 *  - ISSUES. The server's open robot alerts, in the attention list's own
 *    words (`calendarIssues`).
 */
import { useCallback, useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { adaptAlert } from '../../../api/adapters';
import { useAlerts } from '../../../api/hooks';
import {
  schedulesKey,
  useRefreshSchedules,
  useRunOccurrenceNow,
  useSetScheduleEnabled,
  useSkipOccurrence,
} from '../../../api/hooks/useSchedules';
import type { ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import { useSafeSiteId } from '../../../state/useActiveSite';
import { audienceFor } from '../../../auth/roles';
import { useAuth } from '../../../auth/useAuth';
import { useActionsOff } from '../../../auth/useRolePreview';
import { FireAlarmContext } from '../../../state/fire-alarm.context';
import type { SiteTimeZone } from '../../../state/attention/words';
import { actionErrorWords, calendarIssues, type CalendarIssue } from './phone-model';
import { batchFailureText, eachInTurn, toggleFailureWords } from './plan-batch';

export interface ScheduleActionStatus {
  pending: boolean;
  /** What the last action came to, in words; null before any. */
  message: string | null;
  tone: 'ok' | 'error' | 'hold';
}

export interface ScheduleActions {
  /** Each present only when this viewer may edit schedules. */
  startNow?: (o: ScheduleOccurrence) => void;
  skip?: (o: ScheduleOccurrence) => void;
  /** `alsoIds`: more schedules to pause or resume after it, in turn --
   *  the old time an edit kept for it ("Pause both", `KeptPauseConfirm`). */
  setEnabled?: (scheduleId: string, enabled: boolean, alsoIds?: readonly string[]) => void;
  /** "View as" preview: actions are drawn but off. */
  actionsOff: boolean;
  /** Fire mode holds the site: nothing can be started. */
  fireHold: boolean;
  status: ScheduleActionStatus | null;
  resetStatus: () => void;
}

/** What a Pause or Resume that went through says: of one schedule, or
 *  (`many`) of the rows of one plan together. */
export function toggledWords(enabled: boolean, many: boolean): string {
  if (enabled) return many ? 'Schedules resumed.' : 'Schedule resumed.';
  return many
    ? 'Schedules paused. Their times and history are kept.'
    : 'Schedule paused. Its times and history are kept.';
}

/** What a Pause or Resume came to, for a status line: the words, and
 *  whether it went through. */
export interface ToggleOutcome {
  message: string;
  tone: ScheduleActionStatus['tone'];
}

export const FIRE_HOLD_LINE =
  'Held by fire mode: nothing can be started until the alarm is resolved.';
/** "Start now" of a High run that went as Normal: started by someone who
 *  may not set High, further ahead than today's runs and the next one
 *  (`priority_lowered`, review PRIO-R1, 2026-10-31). */
export const RUN_LOWERED_LINE =
  'Sent to the fleet as Normal. Only a supervisor can start a High run this far ahead.';
export const PREVIEW_LINE = 'Viewing as another role: look only.';

export function useScheduleActions(
  scheduleEditsAllowed: boolean,
  timeZone = 'UTC',
): ScheduleActions {
  const actionsOff = useActionsOff();
  const fireHold = useContext(FireAlarmContext)?.isFireAlarmActive === true;
  const runNow = useRunOccurrenceNow();
  const skip = useSkipOccurrence();
  const setEnabled = useSetScheduleEnabled();
  const refresh = useRefreshSchedules();
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  const [lastAction, setLastAction] = useState<'run' | 'skip' | 'enable' | 'plan' | null>(null);
  /** "Pause both" / "Resume all": the rows of one plan, in turn. */
  const [plan, setPlan] = useState<ScheduleActionStatus | null>(null);
  const reset = useCallback(() => {
    setLastAction(null);
    setPlan(null);
    runNow.reset();
    skip.reset();
    setEnabled.reset();
  }, [runNow, skip, setEnabled]);

  const status: ScheduleActionStatus | null = (() => {
    if (lastAction === 'plan') return plan;
    const m =
      lastAction === 'run'
        ? runNow
        : lastAction === 'skip'
          ? skip
          : lastAction === 'enable'
            ? setEnabled
            : null;
    if (!m) return null;
    if (m.isPending) return { pending: true, message: null, tone: 'ok' };
    if (m.isError) {
      // Pause or Resume of one schedule says which it was: "Not paused:".
      return {
        pending: false,
        ...(lastAction === 'enable'
          ? toggleFailureWords(setEnabled.variables?.enabled === true, m.error)
          : actionErrorWords(m.error)),
      };
    }
    if (m.isSuccess) {
      const message =
        lastAction === 'run'
          ? runNow.data?.priority_lowered
            ? RUN_LOWERED_LINE
            : 'Sent to the fleet. It shows as a task once a robot takes it.'
          : lastAction === 'skip'
            ? 'Skipped this time. The schedule keeps running.'
            : toggledWords(setEnabled.variables?.enabled === true, false);
      return { pending: false, message, tone: 'ok' };
    }
    return null;
  })();

  return {
    actionsOff,
    fireHold,
    status,
    resetStatus: reset,
    ...(scheduleEditsAllowed
      ? {
          startNow: (o: ScheduleOccurrence) => {
            if (actionsOff || fireHold) return;
            reset();
            setLastAction('run');
            runNow.mutate({ scheduleId: o.schedule_id, originalStartMs: o.original_start_ms });
          },
          skip: (o: ScheduleOccurrence) => {
            if (actionsOff) return;
            reset();
            setLastAction('skip');
            skip.mutate({ scheduleId: o.schedule_id, originalStartMs: o.original_start_ms });
          },
          setEnabled: (scheduleId: string, enabled: boolean, alsoIds: readonly string[] = []) => {
            if (actionsOff) return;
            reset();
            if (alsoIds.length === 0) {
              setLastAction('enable');
              setEnabled.mutate({ scheduleId, enabled });
              return;
            }
            // One after another, every one tried; the rows are named
            // from the list as it was before.
            setLastAction('plan');
            setPlan({ pending: true, message: null, tone: 'ok' });
            const before = qc.getQueryData<TaskSchedule[]>(schedulesKey(siteId)) ?? [];
            const now = Date.now();
            void eachInTurn([scheduleId, ...alsoIds], (id) =>
              setEnabled.mutateAsync({ scheduleId: id, enabled }),
            ).then((outcome) => {
              const failed = batchFailureText(
                enabled ? 'resume' : 'pause',
                outcome,
                before,
                now,
                timeZone,
              );
              if (failed !== null) void refresh();
              setPlan(
                failed !== null
                  ? { pending: false, message: failed, tone: 'error' }
                  : { pending: false, message: toggledWords(enabled, true), tone: 'ok' },
              );
            });
          },
        }
      : {}),
  };
}

const NO_ALERTS: never[] = [];

/** Open robot alerts about calendar work, as `calendarIssues` words them. */
export function useCalendarIssues(args: {
  timeZone: string;
  timeZoneSource: SiteTimeZone['source'];
  nowMs: number;
  nameGroup: ReadonlyMap<string, string>;
}): CalendarIssue[] {
  const { timeZone, timeZoneSource, nowMs, nameGroup } = args;
  const auth = useAuth();
  const alertsQ = useAlerts();
  const alertRows = useMemo(
    () => (alertsQ.data.length ? alertsQ.data.map(adaptAlert) : NO_ALERTS),
    [alertsQ.data],
  );
  const audience = audienceFor(auth.roles);
  return useMemo(
    () =>
      calendarIssues(alertRows, {
        audience,
        clock: { timeZone, source: timeZoneSource },
        wallNowMs: nowMs,
        nameGroup,
      }),
    [alertRows, audience, timeZone, timeZoneSource, nowMs, nameGroup],
  );
}
